var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as emailValidator from 'email-validator';
import axios from 'axios';
import { Button, CircularProgress, CssBaseline, Grid, TextField, Typography } from '@material-ui/core';
import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { authInstance } from '../../firebase';
import { getSocketIfExist } from '../../utils/getSocket';
import { firebaseFunctionsEndpoint } from '../../utils/http';
import { sendEmailQueryMessage } from '../../utils/ws-v1';
import ScribeLoginEmailVerification from './ScribeLoginEmailVerification';
const styles = () => {
    return {
        forgotDisabled: {
            color: 'grey',
            display: 'inline-block',
            cursor: 'not-allowed',
        },
        forgotEnabled: {
            color: '#77B8EC',
            cursor: 'pointer',
            display: 'inline-block',
            textDecoration: 'underline',
        },
        form: {
            marginTop: 4,
            width: '100%', // Fix IE 11 issue.
        },
        paper: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: 420,
        },
        submit: {
            boxShadow: 'none',
            color: 'white',
            fontWeight: 600,
            margin: '12px 0 8px',
        },
        title: {
            marginTop: 32,
            fontWeight: 700,
        },
        inputLabel: {
            fontWeight: 400,
        },
    };
};
class ScribeLoginEmail extends React.Component {
    constructor(props) {
        super(props);
        this.validateEmail = (email) => {
            return emailValidator.validate(email);
        };
        this.validatePassword = (password) => {
            return password && password.length >= 6;
        };
        this.handleLoginCustomMail = (event) => __awaiter(this, void 0, void 0, function* () {
            event.preventDefault();
            const { email, password } = this.state;
            this.setState({ loading: true });
            try {
                yield authInstance.setPersistence('local');
                yield authInstance.signInWithEmailAndPassword(email, password);
            }
            catch (err) {
                switch (err.code) {
                    case 'auth/user-not-found':
                        yield authInstance.createUserWithEmailAndPassword(email, password);
                        yield this.sendVerificationEmailIfNeeded();
                        break;
                    case 'auth/wrong-password':
                        this.setState({ authError: 'wrongPassword', loading: false });
                        break;
                    default:
                        this.setState({ loading: false });
                        Sentry.captureException(err);
                }
            }
        });
        this.handleUserQueryResponse = (message) => {
            const data = JSON.parse(message.data);
            if (data.type === 'user-query-response') {
                const { usedEmails } = this.state;
                this.setState({
                    usedEmails: [...usedEmails, ...data.users.map((u) => u.email)],
                });
            }
        };
        this.state = {
            authError: '',
            email: '',
            emailToShow: '',
            emailInvalid: false,
            loading: false,
            password: '',
            passwordInvalid: false,
            resetPasswordEmailSent: false,
            usedEmails: [],
            verificationEmailSent: false,
            waitForEmailVerification: false,
        };
    }
    sendVerificationEmailIfNeeded() {
        return __awaiter(this, void 0, void 0, function* () {
            const { verificationEmailSent } = this.state;
            const user = authInstance.currentUser;
            if (!verificationEmailSent && user) {
                this.setState({ loading: true });
                yield axios.post(`${firebaseFunctionsEndpoint}/sendEmailVerification?uid=${user.uid}`);
                this.setState({ loading: false });
                this.setState({
                    verificationEmailSent: true,
                    waitForEmailVerification: true,
                });
            }
        });
    }
    reset() {
        return __awaiter(this, void 0, void 0, function* () {
            this.setState({ loading: false });
            yield authInstance.signOut();
            this.setState({
                authError: undefined,
                email: undefined,
                loading: false,
                password: undefined,
                verificationEmailSent: false,
                waitForEmailVerification: false,
            });
        });
    }
    resetPassword() {
        return __awaiter(this, void 0, void 0, function* () {
            const { checkProvidersForPasswordReset } = this.props;
            const { email } = this.state;
            if (this.validateEmail(email)) {
                const hasOtherProviders = yield checkProvidersForPasswordReset(email);
                if (!hasOtherProviders) {
                    this.setState({ loading: true });
                    try {
                        yield authInstance.sendPasswordResetEmail(email);
                    }
                    finally {
                        this.setState({ resetPasswordEmailSent: true, loading: false });
                    }
                }
            }
        });
    }
    disabled() {
        const { email, password } = this.state;
        return !this.validateEmail(email) || !this.validatePassword(password);
    }
    handleEmailChange(email) {
        this.setState({ emailToShow: email, email: email.toLowerCase() });
        if (this.validateEmail(email)) {
            const ws = getSocketIfExist();
            if (ws) {
                sendEmailQueryMessage(ws, email);
            }
        }
    }
    componentDidMount() {
        return __awaiter(this, void 0, void 0, function* () {
            const ws = getSocketIfExist();
            if (ws) {
                ws.addEventListener('message', this.handleUserQueryResponse);
            }
        });
    }
    componentWillUnmount() {
        const ws = getSocketIfExist();
        if (ws) {
            ws.removeEventListener('message', this.handleUserQueryResponse);
        }
    }
    render() {
        const { classes, t } = this.props;
        const { authError, email, emailToShow, emailInvalid, loading, password, passwordInvalid, resetPasswordEmailSent, usedEmails, waitForEmailVerification, } = this.state;
        if (waitForEmailVerification) {
            return (React.createElement(ScribeLoginEmailVerification, { email: email, loading: loading, reset: () => this.reset('userResetDuringLoader') }));
        }
        if (resetPasswordEmailSent) {
            return (React.createElement(Typography, { variant: "body1", align: "center" }, t('signin-messages.resetLinkSent', { email })));
        }
        return (React.createElement("div", null,
            React.createElement(CssBaseline, null),
            React.createElement("div", { className: classes.paper },
                React.createElement(Typography, { variant: "h4", className: classes.title, gutterBottom: true }, t('signInSignUp')),
                React.createElement("form", { className: classes.form, onSubmit: (event) => this.handleLoginCustomMail(event), noValidate: true },
                    React.createElement(TextField, { variant: "outlined", margin: "normal", color: "primary", required: true, fullWidth: true, id: "email", label: "Email Address", name: "email", autoComplete: "email", autoFocus: true, value: emailToShow || '', onChange: (event) => this.handleEmailChange(event.target.value), error: !!authError || emailInvalid }),
                    React.createElement(TextField, { variant: "outlined", margin: "normal", required: true, fullWidth: true, name: "password", label: "Password", type: "password", id: "password", disabled: !this.validateEmail(email), autoComplete: "current-password", value: password || '', onChange: (event) => this.setState({ password: event.target.value }), helperText: authError && t(`signin-messages.${authError}`), error: !!authError || passwordInvalid }),
                    React.createElement(Button, { type: "submit", fullWidth: true, variant: "contained", color: "primary", className: classes.submit, disabled: this.disabled() || loading, "data-qa": "emailLogin:getStartedButton" },
                        loading && React.createElement(CircularProgress, { color: "secondary", size: "1.6em" }),
                        !loading && t('getStarted')),
                    React.createElement(Grid, { container: true },
                        React.createElement(Grid, { item: true, xs: true, style: { textAlign: 'right' } },
                            React.createElement(Typography, { onClick: () => this.resetPassword(), variant: "body2", className: classes[this.validateEmail(email) ? 'forgotEnabled' : 'forgotDisabled'] }, t('signin-messages.forgotPassword'))))))));
    }
}
export default withTranslation()(withStyles(styles)(ScribeLoginEmail));
